import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, User, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import api, { API } from "@/lib/api";
import Layout from "@/components/Layout";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch (_e) {
    return "";
  }
}

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    api.get(`/public/blog/${id}`)
      .then(({ data }) => { if (!cancel) setPost(data); })
      .catch(() => { if (!cancel) setError("Post not found."); });
    return () => { cancel = true; };
  }, [id]);

  const safeHtml = useMemo(() => {
    if (!post?.content) return "";
    return DOMPurify.sanitize(post.content, {
      ALLOWED_TAGS: ["p","br","strong","em","s","code","blockquote","ul","ol","li","h1","h2","h3","a"],
      ALLOWED_ATTR: ["href","target","rel"],
    });
  }, [post?.content]);

  return (
    <Layout variant="public">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-bold mb-6 hover:underline" data-testid="back-to-blog">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>

        {error ? (
          <div className="brut-card-static p-10 text-center" data-testid="post-error">
            <div className="font-display text-2xl font-black mb-2">Oops</div>
            <p className="text-zinc-700">{error}</p>
          </div>
        ) : !post ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C8102E]" /></div>
        ) : (
          <article className="brut-card-static overflow-hidden" data-testid="post-article">
            {post.cover && (
              <div className="border-b-2 border-black bg-zinc-100 aspect-video">
                <img src={`${API}/files/${post.cover.id}`} alt="" className="w-full h-full object-cover object-center" />
              </div>
            )}
            <div className="p-8 sm:p-10">
              <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-4">{post.title}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-700 font-bold mb-6 pb-4 border-b-2 border-black">
                <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{formatDate(post.publish_at || post.published_at)}</span>
                <span className="flex items-center gap-1.5"><User className="h-4 w-4" />{post.author_name}</span>
              </div>
              <div className="blog-body text-zinc-800" data-testid="post-content" dangerouslySetInnerHTML={{ __html: safeHtml }} />
            </div>
          </article>
        )}
      </div>
    </Layout>
  );
}
